/* =============================================================================
   UTILS / PLAN-FIT — Fit-to-Container-Scaling für den geteilten Plan-Canvas
   =============================================================================
   S207-HOTFIX 3 — DOM-Seite zum window-freien computePlanScale (core/layout-render).
   Miss-Wrapper `.plan-fit-outer` liefert die verfügbare Breite; `.plan-fit` wird auf
   die SICHTBARE Größe (canvas*scale) gesetzt + zentriert; `.plan-fit-inner` (der
   echte Canvas in Editor-Pixeln) bekommt transform:scale. Kein Clipping, keine
   Koordinaten-Umrechnung nötig — Klick-Ziele sitzen auf den Kacheln selbst und
   werden durch das CSS-Transform korrekt mit-skaliert.

   Genutzt von: Gast-Popup (customer-plan), Picker (assignment-picker), Belegungs-
   Snapshot (occupancy). Der Editor bleibt UNSKALIERT (echte Pixel fürs Draggen).
============================================================================ */

import { computePlanScale } from '../core/layout-render.js';

let _resizeBound = false;
let _rafPending = false;

/** Viewport-Höhen-Default (px), wenn kein data-max-h gesetzt ist. */
function _defaultMaxH() {
  const h = (typeof window !== 'undefined' && window.innerHeight) ? window.innerHeight : 800;
  return Math.round(h * 0.72);
}

/** Höhen-Deckel aus dem data-Attribut lesen. '0' = keine Deckelung (Snapshot). */
function _maxHFor(fitEl) {
  const raw = fitEl.getAttribute('data-max-h');
  if (raw === '0') return 0;
  if (raw && Number(raw) > 0) return Number(raw);
  return _defaultMaxH();
}

/** Ein einzelnes .plan-fit-Element auf seinen Container einpassen. */
export function fitPlanEl(fitEl) {
  if (!fitEl) return;
  const inner = fitEl.querySelector('.plan-fit-inner');
  const outer = fitEl.parentElement;   // .plan-fit-outer = gemessener Container
  if (!inner || !outer) return;
  const cw = Number(fitEl.getAttribute('data-canvas-w')) || 0;
  const ch = Number(fitEl.getAttribute('data-canvas-h')) || 0;
  if (!cw || !ch) return;
  const avail = outer.clientWidth || cw;
  const { scale, scroll } = computePlanScale(cw, ch, avail, _maxHFor(fitEl));
  inner.style.transform = scale < 1 ? `scale(${scale})` : 'none';
  // .plan-fit auf die SICHTBARE Größe setzen → normaler Fluss-Kasten (zentrierbar via
  // margin:auto), overflow:hidden klippt den ungenutzten Layout-Überhang des Inners.
  fitEl.style.width = Math.round(cw * scale) + 'px';
  fitEl.style.height = Math.round(ch * scale) + 'px';
  // scroll=true (unter Mindest-Skala): sichtbare Breite kann > avail sein → Container
  // scrollt horizontal statt weiter zu schrumpfen.
  outer.classList.toggle('plan-fit-scrolling', !!scroll);
}

/** Alle .plan-fit-Elemente unterhalb eines Roots (oder im ganzen Dokument) einpassen. */
export function fitAllPlans(root) {
  if (typeof document === 'undefined') return;
  const scope = (root && root.querySelectorAll) ? root : document;
  scope.querySelectorAll('.plan-fit').forEach(fitPlanEl);
  _ensureResizeListener();
}

// ---------------------------------------------------------------------------
// S207-HOTFIX 5 — Modal-BREITE folgt der gecroppten Skizze (kein Leerraum bei
// kleinen Räumen). Die sichtbare Ziel-Breite = clamp(minW, bboxW + Chrome, maxW)
// wird VOR dem Fit bestimmt (bboxW liegt als data-canvas-w am .plan-fit vor → kein
// Henne-Ei mit der Container-Messung) und dem Panel gesetzt; DANN fitPlanEl.
// ---------------------------------------------------------------------------

/** Ziel-Breite aus dem enthaltenen .plan-fit (bboxW) + hinterlegten Grenzen setzen. */
function _applyPanelWidth(panelEl) {
  const fit = panelEl.querySelector('.plan-fit');
  const bboxW = fit ? (Number(fit.getAttribute('data-canvas-w')) || 0) : 0;
  const minW = Number(panelEl.dataset.planMinW) || 420;
  const maxPx = Number(panelEl.dataset.planMaxPx) || 1100;
  const maxVw = Number(panelEl.dataset.planMaxVw) || 92;
  const chrome = Number(panelEl.dataset.planChrome) || 40;
  const vw = (typeof window !== 'undefined' && window.innerWidth) ? window.innerWidth : 1200;
  const maxW = Math.min(maxPx, Math.round(vw * maxVw / 100));
  panelEl.style.maxWidth = maxW + 'px';
  if (!bboxW) { panelEl.style.width = ''; return; }   // kein Plan (z. B. Vorschläge-Tab) → CSS-Default
  panelEl.style.width = Math.round(Math.max(minW, Math.min(bboxW + chrome, maxW))) + 'px';
}

/**
 * Panel (Modal) so breit machen wie die gecroppte Skizze braucht. `opts` wird als
 * data-Attribut hinterlegt → beim Resize re-anwendbar (ein geteilter Listener).
 * @param {Element} panelEl
 * @param {{minW?:number, maxWpx?:number, maxWvw?:number, chrome?:number}} opts
 */
export function sizePlanPanel(panelEl, opts) {
  if (!panelEl) return;
  const o = opts || {};
  panelEl.dataset.planMinW = String(o.minW || 420);
  panelEl.dataset.planMaxPx = String(o.maxWpx || 1100);
  panelEl.dataset.planMaxVw = String(o.maxWvw || 92);
  panelEl.dataset.planChrome = String(o.chrome || 40);
  _applyPanelWidth(panelEl);
}

/** Alle markierten Panels neu breit rechnen (Resize/Segment-Wechsel). */
export function resizeAllPlanPanels() {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('[data-plan-min-w]').forEach(_applyPanelWidth);
}

/** Einmaliger, geteilter Resize-Listener (kein Leak) — RAF-gedrosselt. */
function _ensureResizeListener() {
  if (_resizeBound || typeof window === 'undefined') return;
  _resizeBound = true;
  window.addEventListener('resize', () => {
    if (_rafPending) return;
    _rafPending = true;
    window.requestAnimationFrame(() => {
      _rafPending = false;
      resizeAllPlanPanels();   // Panel-Breite (vw-abhängig) zuerst nachziehen …
      fitAllPlans(document);   // … dann den Plan neu einpassen
    });
  });
}

if (typeof window !== 'undefined') {
  Object.assign(window, { fitAllPlans, fitPlanEl, sizePlanPanel, resizeAllPlanPanels });
}
